import {Injectable} from '@angular/core';
import {HttpClient, HttpEvent, HttpHeaders, HttpRequest} from "@angular/common/http";
import {SubauthorityData} from "../model/subauthorityData";
import {forkJoin, Observable} from "rxjs";
import {BatchDownload} from "../model/batchDownload";
import {Document} from "../model/document";
import {Property} from "../model/property";
import {Order} from "../model/order";
import {environment} from "../../environments/environment";
import {AuthorityData} from "../model/authorityData";
import {NgxIndexedDBService} from "ngx-indexed-db";
import {StorageService} from "./storage.service";
import {OrderService} from "./order.service";
import {IdBuilderUtils} from "../utils/id-builder-utils";
import {User} from "../model/user";
import {PropertyService} from "./property.service";
import {BatchDownloadUtils} from "../utils/batch-download-utils";
import {DocumentFileService} from "./document-file.service";
import {DocumentFileDownloadStatus} from "../model/document-file-download-status";

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  constructor(private http: HttpClient, private dbService: NgxIndexedDBService, private storageService: StorageService,
              private orderService: OrderService, private propertyService: PropertyService,
              private documentFileService: DocumentFileService) {
  }

  downloadSubAuthority(subAuthorityData: SubauthorityData) {
    this.openWindowWithPost(environment.apiUrl2 + '/api/signed-sub-authority/', {
      subauthorityData: JSON.stringify(subAuthorityData)
    })
  }

  downloadAuthority(authorityData: AuthorityData) {
    this.openWindowWithPost(environment.apiUrl2 + '/api/signed-authority/', {
      authorityData: JSON.stringify(authorityData)
    })
  }


  downloadAuthorityTemplate() {
    window.open(environment.apiUrl2 + '/api/downloadAuthorityTemplate/', '_self')
  }

  downloadDocumentFileById(documentFileId) {
    this.http.get('/api/download/documentFile/' + documentFileId, {
      observe: 'response',
      responseType: 'blob'
    }).subscribe(res => {
      this.downloadBlobFromUrl(res)
    });
  }

  /**
   * Download a PDF authority template for a specific authority type
   *
   * @param authorityType (Optional) Type of the authority that should be downloaded
   */
  public downloadAuthorityPdfTemplate(authorityType: string | null): void {
    let url = "/api/download/authority-template/pdf"

    if(authorityType){
      url += `?authorityType=${authorityType}`;
    }

    this.http.get(url, {
      observe: 'response',
      responseType: 'blob'
    }).subscribe(res => {
      this.downloadBlobFromUrl(res)
    })
  }

  /**
   * Downloads all documentFiles for a Document.
   * If the total file size of all files is too high (> 50MB) the Documents are not downloaded directly but via a batch download
   *
   * @param document Document for which all files should be downloaded
   * @param forceBatchDownload Boolean which determines if the batch download should be forced to start (ignores all other checks)
   * @return true if a batch download was started else false
   */
  public async downloadDocument(document: Document, forceBatchDownload: boolean = false): Promise<boolean> {
    try {
      const documentFileSize = await this.checkDocumentFileSize(document, true);
      if ((documentFileSize > 50_000_000 && document.files.length > 1) || forceBatchDownload) {
        return this.startBatchDownload(document.docid, false, [])
      } else {
        this.downloadDoc(document.docid, false);
        return false;
      }
    } catch (error) {
      // If an error is thrown start the batch download since we dont know the file size. This is done so at least any download is started
      return this.startBatchDownload(document.docid, false, [])
    }
  }

  /**
   * Initiates a batch download process based on provided parameters
   *
   * @param id Either a Document, Property or Order ID for which the batch download should be started
   * @param onlyNew Boolean which determines if only new documents or all documents should be downloaded
   * @param groupIds A list of file group ids which are used to filter which document files of which groups should be downloaded
   * @private Returns true to indicate that the batch download was just started or was started less than 5 minutes ago
   */
  private startBatchDownload(id: string, onlyNew: boolean, groupIds: string[]): boolean {
    const batchDownloadKey = groupIds.length > 0 ? `${groupIds.join("_")}_${onlyNew}` : `${id}_${onlyNew}`;
    const batchDownloadTimestamp = BatchDownloadUtils.getTimestamp(batchDownloadKey);
    if (batchDownloadTimestamp === -1 || BatchDownloadUtils.isTimeStampOlderThanFiveMinutes(batchDownloadTimestamp)) {
      BatchDownloadUtils.saveTimestamp(batchDownloadKey);
      this.batchDownload(id, onlyNew, groupIds).subscribe();
    }
    return true;
  }

  /**
   * Downloads all documentFiles for all Documents of a Property.
   * If the total file size of all files is too high (> 50MB) the Documents are not downloaded directly but bia a batch download.
   *
   * @param property Property for which all files should be downloaded
   * @param onlyNew Boolean which determines if only new documents or all documents should be downloaded
   * @param usePropertyStatistics Boolean which determines if the propertyStatistics should be used to determine if a batch download should be started. Default is true
   * @param forceBatchDownload Boolean which determines if the batch download should be forced to start (ignores all other checks)
   * @return true if a batch download was started else false
   */
  async downloadForProperty(property: Property, onlyNew = false, usePropertyStatistics: boolean = true, forceBatchDownload: boolean = false) {
    try {
      if (forceBatchDownload) {
        return this.startBatchDownload(property.id, onlyNew, [])
      }
      let propertyFileSize: number;
      if (usePropertyStatistics) {
        propertyFileSize = onlyNew ? property.propertyStatistics.newSumOfPropertyFileSize : property.propertyStatistics.totalSumOfPropertyFileSize;
      } else {
        propertyFileSize = await this.checkPropertyFileSize(property, onlyNew, null);
      }
      if (propertyFileSize < 50_000_000) {
        this.downloadDoc(property.id, onlyNew);
        return false;
      } else {
        return this.startBatchDownload(property.id, onlyNew, [])
      }
    } catch (error) {
      // If an error is thrown start the batch download since we dont know the file size. This is done so at least any download is started
      return this.startBatchDownload(property.id, onlyNew, []);
    }
  }

  getAllBatchDownloadsForUser(): Observable<any> {
    return this.http.get("/api/largeDownloads");
  }

  /**
   * Creates a batch-download for a specific document, property or order ID
   *
   * @param id Either a Document, Property or Order ID
   * @param onlyNew Boolean which determines if only new documents or all documents should be downloaded.
   * @param groupIds A list of file group ids which are used to filter which document files of which groups should be downloaded
   * @return
   */
  public batchDownload(id, onlyNew, groupIds: string[]): Observable<BatchDownload> {
    return this.http.post<BatchDownload>("/api/batchDownload?id=" + id + "&onlyNew=" + onlyNew + "&documentFileGroupIds=" + groupIds.join(","), {
      observe: 'response',
      responseType: 'blob',
      withCredentials: true
    }, {headers: new HttpHeaders({'showing-spinner': ''})});
  }

  downloadDoc(documentId, onlyNew) {
    let orderId = IdBuilderUtils.buildOrderIdFromDocumentOrPropertyId(documentId);
    this.http.get('/api/download/' + documentId + "?onlyNew=" + onlyNew, {
      observe: 'response',
      responseType: 'blob'
    }).subscribe(res => {
      this.downloadBlobFromUrl(res)

      this.dbService.getByKey('orders', orderId).subscribe(async (orderFromIndexedDb: Order) => {
        let order: Order;
        if (orderFromIndexedDb) {
          order = orderFromIndexedDb
        } else {
          order = await this.orderService.getChildlessOrderById(orderId, {headers: new HttpHeaders({'showing-spinner': ''})}).toPromise();
        }

        this.updateOrderProperties(order);
      })
    });
  }

  /**
   * Update the properties of the given order. If the order does not have any properties query all childless properties for this order first.
   * After that the orderStatistics as well as the propertyStatistics are being updated
   *
   * @param order Order for which the properties should be updated
   */
  private updateOrderProperties(order: Order): void {
    if (!order.properties || order.properties.length === 0) {
      this.propertyService.getChildlessPropertiesByOrder(order.orderid, {headers: new HttpHeaders({'showing-spinner': ''})}).subscribe(childlessProperties => {
        order.properties = childlessProperties;
        this.updateStatistics(order);
      })
    } else {
      this.updateStatistics(order)
    }
  }

  /**
   * Update the orderStatistics and propertyStatistics for a given order. After the statistics are updated, the order is being saved in the indexedDb
   *
   * @param order Order for which the orderStatistics and propertyStatistics should be updated
   */
  private updateStatistics(order: Order): void {
    const orderId = order.orderid;
    const getPropertyStatisticsObservable = this.propertyService.getPropertyStatisticsForOrderProperties(orderId, {headers: new HttpHeaders({'showing-spinner': ''})});
    const getOrderStatisticsObservable = this.orderService.getOrderStatistics(orderId, {headers: new HttpHeaders({'showing-spinner': ''})});

    forkJoin([getPropertyStatisticsObservable, getOrderStatisticsObservable]).subscribe(([retrievedPropertyStatistics, retrivedOrderStatistics]) => {
      order.orderStatistics = retrivedOrderStatistics;
      for (const propertyStatistics of retrievedPropertyStatistics) {
        const propertyIndex = order.properties.findIndex(property => property.id === propertyStatistics.propertyId)
        order.properties[propertyIndex].propertyStatistics = propertyStatistics;
      }
      this.storageService.setIndexedDbItem({key: 'orders', value: order}).then()
    })
  }

  /**
   * Downloads all documentFiles for all Documents of a Property where the document files groupId is inside the groupIds array.
   * If the total file size of all files is too high (> 50MB) the Documents are not downloaded directly but via a batch download.
   *
   * @param property Property for which all files should be downloaded
   * @param groupIds A list of file group ids which are used to filter which document files of which groups should be downloaded
   * @param onlyNew Boolean which determines if only new documents or all documents should be downloaded
   * @param forceBatchDownload Boolean which determines if the batch download should be forced to start (ignores all other checks)
   */
  public async downloadDocumentFilesByGroupIds(property: Property, groupIds: string[], onlyNew: boolean, forceBatchDownload: boolean = false): Promise<boolean> {
    try {
      const documentFileSize = await this.checkPropertyFileSize(property, onlyNew, groupIds);
      if ((documentFileSize < 50_000_000) && !forceBatchDownload) {
        this.downloadDocumentFilesByGroupIdsAndProperty(property.id, onlyNew, groupIds);
        return false;
      } else {
        return this.startBatchDownload(property.id, onlyNew, groupIds);
      }
    } catch (error) {
      // If an error is thrown start the batch download since we dont know the file size. This is done so at least any download is started
      return this.startBatchDownload(property.id, onlyNew, groupIds);
    }
  }

  /**
   * Gets the document files for a specific property and for specific groupIds as a blob and starts the download
   * @param propertyId Id of a property for which the document files should be downloaded
   * @param onlyNew Boolean which determines if only new documents or all documents should be downloaded
   * @param groupIds A list of file group ids which are used to filter which document files of which groups should be downloaded
   */
  public downloadDocumentFilesByGroupIdsAndProperty(propertyId: string, onlyNew: boolean, groupIds: string[]): void {
    this.http.get("/api/download/" + propertyId + "?onlyNew=" + onlyNew + "&documentFileGroupIds=" + groupIds.join(","), {
      observe: 'response',
      responseType: 'blob'
    }).subscribe(res => {
      this.downloadBlobFromUrl(res);
    })
  }

  downloadBlobFromUrl(downloadResult) {
    let blob: Blob = downloadResult.body
    let a = document.createElement('a');
    let url = window.URL.createObjectURL(blob);
    a.download = this.getFilenameFromContentDisposition(downloadResult.headers.get('content-disposition'))
    a.href = url
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  /**
   * Downloads all documentFiles for all Documents of all Properties of an Order.
   * If the total file size of all files is too high (> 50MB) the Documents are not downloaded directly but via a batch download
   *
   * @param order Order for which all files should be downloaded
   * @param onlyNew Boolean which determines if only new documents or all documents should be downloaded
   * @return true if a batch download was started else false
   */
  downloadForOrder(order: Order, onlyNew = false) {
    const orderFileSize = onlyNew ? order.orderStatistics.newSumOfOrderFileSize : order.orderStatistics.totalSumOfOrderFileSize;
    if (orderFileSize && orderFileSize > 50_000_000) {
      return this.startBatchDownload(order.orderid, onlyNew, []);
    } else {
      this.downloadDoc(order.orderid, onlyNew)
      return false;
    }
  }


  getFilenameFromContentDisposition(contentDisposition: string): string {

    let i = contentDisposition.indexOf('filename=')
    if (i < 0)
      return "document.pdf";
    return contentDisposition.substr(i + 9);
  }


  downloadBatchFile(id: string): Observable<HttpEvent<Blob>> {
    const req = new HttpRequest('GET', `/api/download/batch/${id}`, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders({'showing-spinner': ''})
    });

    return this.http.request(req);
  }

  getBatchDownload(id): Observable<BatchDownload> {
    return this.http.get<BatchDownload>("/api/batchDownload/" + id);
  }

  /**
   * Calculates the total file size for all document files of all documents of a property
   *
   * @param property Property for which the total file size should be calculated
   * @param onlyNew Boolean which determines if only new documents of all documents should be considered
   * @param documentFileGroupIds A list of file group ids which are used to filter which document files of which groups should be considered
   * @return total file size for all considered documents files inside a property
   */
  public async checkPropertyFileSize(property: Property, onlyNew = false, documentFileGroupIds: null | string[]): Promise<number> {
    let size = 0;
    //Filter and calculate only the document file size for document files which have a groupID that is inside documentFileGroupIds
    if (documentFileGroupIds !== null && documentFileGroupIds.length > 0) {
      property.documents.forEach(document => {
        document.files.forEach(file => {
          if (documentFileGroupIds.includes(file.groupId)) {
            size += file.length;
          }
        })
      })
    } else {
      for (const document of property.documents) {
        size += await this.checkDocumentFileSize(document, onlyNew);
      }
    }
    return size;
  }

  /**
   * Calculates the total size od all files for the specified document.
   * Optionally can consider only new files for the current multi-user
   *
   * @param document Document containing the files for which the total file size should be calculated
   * @param onlyNew Boolean which determines if only new (not yet downloaded) files should be considered
   * @return Promise containing the total size of all files in the given document
   */
  async checkDocumentFileSize(document: Document, onlyNew = false) {
    const user: User = JSON.parse(sessionStorage.getItem("currentMultiUser"));
    let size = 0;
    let downloadStatusList: DocumentFileDownloadStatus[] = [];

    // If only new documents should be considered retrieve the download status of all document files of the document
    if (onlyNew) {
      downloadStatusList = await this.documentFileService.getDocumentFileDownloadStatusForFiles(document.files.map(file => file.id)).toPromise();
    }

    outerLoop: for (const documentFile of document.files) {
      if (user && downloadStatusList.length > 0) {
        for (const documentDownloadStatus of downloadStatusList) {
          if (onlyNew && documentDownloadStatus.userId === user.id) {
            continue outerLoop;
          }
        }
      }
      size += documentFile.length
    }
    return size;
  }

  openWindowWithPost(url, data) {
    let form = document.createElement("form");
    form.target = "_self";
    form.method = "POST";
    form.action = url;
    form.style.display = "none";
    form.enctype = "multipart/form-data"


    for (let key in data) {
      let input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = data[key];
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }


}
